import React, { useCallback, useContext, useEffect, useMemo } from 'react';

import { Skeleton } from '@mui/material';
import { useStatsigClient } from '@statsig/react-bindings';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import DefaultHome from './DefaultHome/DefaultHome';
import EmptyHome from './EmptyHome/EmptyHome';
import RFHome from './RFHome/RFHome';
import CustomEmptyPage from '../../shared/components/CustomEmptyPage/CustomEmptyPage';
import HelpUsImproveBanner from '../../shared/components/v3/HelpUsImproveBanner/HelpUsImproveBanner';
import { MANAGE_COLLABORATORS_ROUTE } from '../../shared/constants/auth';
import { SKELETON_COMPONENT_CLASSES } from '../../shared/constants/home';
import { FIRST_LOGIN_MODAL } from '../../shared/constants/modal';
import { PLAN_TYPE_FR, PLAN_TYPE_HEALTH } from '../../shared/constants/plan';
import { CAN_SEE_RF_HOME_FG } from '../../shared/constants/statsig';
import { CLICK_ACTION, MANAGE_COLLABORATORS } from '../../shared/constants/tagManager';
import { SessionContext } from '../../shared/context/auth/authContext';
import { CompanyContext } from '../../shared/context/company/companyContext';
import { ModalContext } from '../../shared/context/modal/modalContext';
import TagManagerService from '../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_HOME } from '../../shared/translations/home';
import { isEmptyObject } from '../../shared/utils/validation/validation';

import './HomeNE.scss';

// eslint-disable-next-line react/display-name
const HomeSkeleton = React.memo(() => (
  <div className="home-container">
    {SKELETON_COMPONENT_CLASSES.map((className) => (
      <div className={className} key={className}>
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: '32px' }} />
      </div>
    ))}
  </div>
));

const HomeNE = () => {
  const { companyData, firstLogin, companyPlansType, setUserFirstLogin, isUSA } = useContext(CompanyContext);
  const isPlanTypeHealth = companyPlansType === PLAN_TYPE_HEALTH;
  const { handleCloseModal, handleOpenModal } = useContext(ModalContext);
  const { isBetterflyer } = useContext(SessionContext);

  const { t } = useTranslation();
  const { client } = useStatsigClient();
  const navigate = useNavigate();

  const goToManageCollaborators = useCallback(() => {
    handleCloseModal();
    navigate(MANAGE_COLLABORATORS_ROUTE);
    TagManagerService.pushEvent(CLICK_ACTION, MANAGE_COLLABORATORS);
  }, [handleCloseModal, navigate]);

  const firstLoginModalConfig = useMemo(() => {
    const modalCopy = { ...FIRST_LOGIN_MODAL };
    if (modalCopy.primaryButton) {
      modalCopy.primaryButton = { ...modalCopy.primaryButton, action: goToManageCollaborators };
    }
    return modalCopy;
  }, [goToManageCollaborators]);

  useEffect(() => {
    if (firstLogin && !isUSA && !isPlanTypeHealth && companyPlansType) {
      handleOpenModal(firstLoginModalConfig);
      setUserFirstLogin();
    }
  }, [
    firstLogin,
    handleOpenModal,
    setUserFirstLogin,
    firstLoginModalConfig,
    isPlanTypeHealth,
    companyPlansType,
    isUSA
  ]);

  const isCompanyDataReady = !isEmptyObject(companyData);
  const shouldShowSkeleton = !companyPlansType || !client || !isCompanyDataReady;

  const getHomeComponent = () => {
    if (shouldShowSkeleton) {
      return <HomeSkeleton />;
    }
    if (companyPlansType === PLAN_TYPE_HEALTH && !isBetterflyer && !companyData.hasMembers) {
      return <EmptyHome />;
    }
    if (companyPlansType === PLAN_TYPE_FR && client?.checkGate(CAN_SEE_RF_HOME_FG)) {
      return <RFHome />;
    }
    return <DefaultHome />;
  };

  if (!isCompanyDataReady) {
    return (
      <CustomEmptyPage
        title={t(TRANSLATIONS_HOME.errorMessageTitle)}
        subtitle={t(TRANSLATIONS_HOME.emptyMessageDescription)}
      />
    );
  }

  return (
    <div className="home-ne">
      {!shouldShowSkeleton && <HelpUsImproveBanner pageKey="home" />}
      {getHomeComponent()}
    </div>
  );
};

export default HomeNE;
